import { useApiClient } from '../../api/useApiClient';

/**
 * Servicio para el workflow de aprobación de documentos
 * Encapsula todas las operaciones de revisión y aprobación
 */
class WorkflowService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Obtener documentos pendientes de revisión con filtros
     * @param {Object} pagination - Configuración de paginación { page, size }
     * @param {Object} filters - Filtros de búsqueda (opcional)
     * @param {Array} sort - Configuración de ordenamiento (opcional)
     * @returns {Promise} Response con documentos pendientes de revisión
     */
    async getPendingReview(pagination = { page: 0, size: 10 }, filters = {}, sort = []) {
        const { page, size } = pagination;
        const payload = {
            ...filters,
            sort
        };
        return this.apiClient.post(
            `/api/v1/docs/pending/review`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Obtener documentos pendientes de aprobación con filtros
     * @param {Object} pagination - Configuración de paginación { page, size }
     * @param {Object} filters - Filtros de búsqueda (opcional)
     * @param {Array} sort - Configuración de ordenamiento (opcional)
     * @returns {Promise} Response con documentos pendientes de aprobación
     */
    async getPendingApproval(pagination = { page: 0, size: 10 }, filters = {}, sort = []) {
        const { page, size } = pagination;
        const payload = {
            ...filters,
            sort
        };
        return this.apiClient.post(
            `/api/v1/docs/pending/approval`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Obtener documentos pendientes de OCR con filtros
     * @param {Object} pagination - Configuración de paginación { page, size }
     * @param {Object} filters - Filtros de búsqueda (opcional)
     * @param {Array} sort - Configuración de ordenamiento (opcional)
     * @returns {Promise} Response con documentos pendientes de OCR
     */
    async getPendingOcr(pagination = { page: 0, size: 10 }, filters = {}, sort = []) {
        const { page, size } = pagination;
        const payload = {
            ...filters,
            sort
        };
        return this.apiClient.post(
            `/api/v1/docs/pending/ocr`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Obtener documentos rechazados con filtros
     * @param {Object} pagination - Configuración de paginación { page, size }
     * @param {Object} filters - Filtros de búsqueda (opcional)
     * @param {Array} sort - Configuración de ordenamiento (opcional)
     * @returns {Promise} Response con documentos rechazados
     */
    async getRejected(pagination = { page: 0, size: 10 }, filters = {}, sort = []) {
        const { page, size } = pagination;
        const payload = {
            ...filters,
            sort
        };
        return this.apiClient.post(
            `/api/v1/docs/rejected`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Aprobar un documento
     * @param {string} idDoc - ID del documento
     * @returns {Promise} Response con resultado de la aprobación
     */
    async approveDocument(idDoc) {
        return this.apiClient.post(`/api/v1/docs/${idDoc}/approve`);
    }

    /**
     * Rechazar un documento
     * @param {string} idDoc - ID del documento
     * @param {string} reason - Razón del rechazo
     * @param {string} mode - Modo de rechazo: 'review' o 'approval'
     * @returns {Promise} Response con resultado del rechazo
     */
    async rejectDocument(idDoc, reason, mode = 'review') {
        const endpoint = mode === 'approval'
            ? `/api/v1/docs/${idDoc}/reject-approval`
            : `/api/v1/docs/${idDoc}/reject-review`;
        return this.apiClient.post(endpoint, {
            rejectedReason: reason
        });
    }

    /**
     * Completar revisión de un documento
     * @param {string} idDoc - ID del documento
     * @param {Object} metadata - Metadata actualizada (opcional)
     * @returns {Promise} Response con resultado de la revisión
     */
    async completeReview(idDoc, metadata = {}) {
        return this.apiClient.post(`/api/v1/docs/${idDoc}/review`, metadata);
    }

    /**
     * Actualizar metadata durante la revisión
     * @param {string} idDoc - ID del documento
     * @param {Object} metadata - Metadata a actualizar
     * @returns {Promise} Response con resultado de la actualización
     */
    async updateReviewMetadata(idDoc, metadata) {
        return this.apiClient.put(`/api/v1/docs/${idDoc}/review/metadata`, metadata);
    }

    /**
     * Reejecutar OCR en un documento
     * @param {string} idDoc - ID del documento
     * @returns {Promise} Response con resultado de la reejecución
     */
    async rerunOCR(idDoc) {
        return this.apiClient.post(`/api/v1/docs/${idDoc}/rerun-ocr`);
    }

    // ==================== BATCH OPERATIONS (by IDs) ====================

    /**
     * Aprobar múltiples documentos por sus IDs
     * @param {Array<number>} ids - Array de IDs de documentos
     * @returns {Promise} Response con resultado de la aprobación
     */
    async approveDocumentsBatch(ids) {
        return this.apiClient.post('/api/v1/docs/approve/batch', { ids });
    }

    /**
     * Revisar múltiples documentos por sus IDs
     * @param {Array<number>} ids - Array de IDs de documentos
     * @returns {Promise} Response con resultado de la revisión
     */
    async reviewDocumentsBatch(ids) {
        return this.apiClient.post('/api/v1/docs/review/batch', { ids });
    }

    /**
     * Rechazar múltiples documentos en revisión por sus IDs
     * @param {Array<number>} ids - Array de IDs de documentos
     * @param {string} reason - Razón del rechazo
     * @returns {Promise} Response con resultado del rechazo
     */
    async rejectReviewDocumentsBatch(ids, reason) {
        return this.apiClient.post('/api/v1/docs/reject-review/batch', {
            ids,
            rejectedReason: reason
        });
    }

    /**
     * Rechazar múltiples documentos en aprobación por sus IDs
     * @param {Array<number>} ids - Array de IDs de documentos
     * @param {string} reason - Razón del rechazo
     * @returns {Promise} Response con resultado del rechazo
     */
    async rejectApprovalDocumentsBatch(ids, reason) {
        return this.apiClient.post('/api/v1/docs/reject-approval/batch', {
            ids,
            rejectedReason: reason
        });
    }

    // ==================== FILTER-BASED OPERATIONS ====================

    /**
     * Aprobar documentos que coincidan con un filtro
     * @param {Object} filter - Objeto de filtro (SearchDocsRequest)
     * @returns {Promise} Response con resultado de la aprobación
     */
    async approveDocumentsByFilter(filter) {
        // El API discrimina entre batch-by-ids y batch-by-filter por la clave del
        // body ('ids' vs 'filter'). Mandar el filter pelado sin wrappear genera 400.
        return this.apiClient.post('/api/v1/docs/approve/batch', { filter });
    }

    /**
     * Revisar documentos que coincidan con un filtro
     * @param {Object} filter - Objeto de filtro (SearchDocsRequest)
     * @returns {Promise} Response con resultado de la revisión
     */
    async reviewDocumentsByFilter(filter) {
        // El API discrimina entre batch-by-ids y batch-by-filter por la clave del
        // body ('ids' vs 'filter'). Mandar el filter pelado sin wrappear genera 400.
        return this.apiClient.post('/api/v1/docs/review/batch', { filter });
    }

    /**
     * Rechazar documentos en revisión que coincidan con un filtro
     * @param {Object} filter - Objeto de filtro (SearchDocsRequest)
     * @param {string} reason - Razón del rechazo
     * @returns {Promise} Response con resultado del rechazo
     */
    async rejectReviewDocumentsByFilter(filter, reason) {
        return this.apiClient.post('/api/v1/docs/reject-review/batch', {
            filter,
            rejectedReason: reason
        });
    }

    /**
     * Rechazar documentos en aprobación que coincidan con un filtro
     * @param {Object} filter - Objeto de filtro (SearchDocsRequest)
     * @param {string} reason - Razón del rechazo
     * @returns {Promise} Response con resultado del rechazo
     */
    async rejectApprovalDocumentsByFilter(filter, reason) {
        return this.apiClient.post('/api/v1/docs/reject-approval/batch', {
            filter,
            rejectedReason: reason
        });
    }

    // ==================== BATCH TASK STATUS (async, ADR-017) ====================

    /**
     * Obtener el estado actual de una batch task (polling).
     * @param {string} taskId - UUID de la task
     * @returns {Promise} Response con BatchTaskStatus en data
     */
    async getBatchTaskStatus(taskId) {
        return this.apiClient.get(`/api/v1/docs/batch-tasks/${encodeURIComponent(taskId)}`);
    }

    /**
     * Solicitar cancelación cooperativa de una batch task (sólo owner o admin).
     * @param {string} taskId - UUID de la task
     * @returns {Promise} Response con BatchTaskStatus actualizado (cancelled=true)
     */
    async cancelBatchTask(taskId) {
        return this.apiClient.delete(`/api/v1/docs/batch-tasks/${encodeURIComponent(taskId)}`);
    }
}

/**
 * Hook personalizado para usar el servicio de workflow
 * @returns {WorkflowService} Instancia del servicio de workflow
 */
export const useWorkflowService = () => {
    const apiClient = useApiClient();
    return new WorkflowService(apiClient);
};

// Backward compatibility alias
export const useApprovalService = useWorkflowService;

export default WorkflowService;
